
import { motion } from "framer-motion";
import { useEffect, useState, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { PlayIcon, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { format, isToday } from "date-fns";

interface GameRecord {
  id: string;
  created_at: string;
  rounds: number;
  final_word: string | null;
  status: string;
  type: string;
  first_user_word?: string;
  first_ai_word?: string;
}

export const GameHistory = () => {
  const [history, setHistory] = useState<GameRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useCallback((node: HTMLDivElement | null) => {
    if (loading || loadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        fetchMoreGames();
      }
    });
    
    if (node) observerRef.current.observe(node);
  }, [loading, loadingMore, hasMore]);

  const fetchGames = async (startIndex = 0, limit = 10) => {
    try {
      if (startIndex === 0) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const gamesQuery = supabase.from('games').select('*').order('created_at', {
        ascending: false
      }).range(startIndex, startIndex + limit - 1);

      if (user) {
        gamesQuery.eq('user_id', user.id);
      } else {
        gamesQuery.eq('status', 'completed');
      }

      const {
        data: gamesData,
        error: gamesError
      } = await gamesQuery;
      
      if (gamesError) throw gamesError;
      
      if (gamesData) {
        // If we got fewer games than the limit, there are no more to load
        if (gamesData.length < limit) {
          setHasMore(false);
        }
        
        const historyWithDetails = await Promise.all(gamesData.map(async game => {
          const {
            count,
            error: countError
          } = await supabase.from('rounds').select('*', {
            count: 'exact',
            head: true
          }).eq('game_id', game.id);
          if (countError) throw countError;

          const {
            data: firstRoundData,
            error: firstRoundError
          } = await supabase.from('rounds').select('user_word, ai_word').eq('game_id', game.id).eq('round_number', 1).maybeSingle();
          if (firstRoundError) throw firstRoundError;
          return {
            id: game.id,
            created_at: game.created_at,
            rounds: count || 0,
            final_word: game.final_word,
            status: game.status,
            type: game.type || 'Personal',
            first_user_word: firstRoundData?.user_word || undefined,
            first_ai_word: firstRoundData?.ai_word || undefined
          };
        }));
        
        if (startIndex === 0) {
          setHistory(historyWithDetails);
        } else {
          setHistory(prevHistory => [...prevHistory, ...historyWithDetails]);
        }
      }
    } catch (error) {
      console.error('Error fetching game history:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchMoreGames = () => {
    if (!hasMore || loadingMore) return;
    fetchGames(history.length);
  };

  useEffect(() => {
    fetchGames(0);
  }, [user]);

  const navigateToGame = (gameId: string) => {
    navigate(`/game?id=${gameId}`);
  };

  const capitalizeFirstLetter = (str: string): string => {
    return str.replace(/\b\w/g, char => char.toUpperCase());
  };
  
  const formatGameTitle = (game: GameRecord) => {
    if (!game.first_user_word || !game.first_ai_word) {
      return game.status === 'completed' ? `Convergence in ${game.rounds} rounds` : `Game in progress - Round ${game.rounds}`;
    }
    const userWord = capitalizeFirstLetter(game.first_user_word);
    const aiWord = capitalizeFirstLetter(game.first_ai_word);
    if (game.status === 'completed' && game.final_word) {
      const finalWord = capitalizeFirstLetter(game.final_word);
      return `${userWord} <> ${aiWord} ... ${finalWord}!`;
    } else {
      return `${userWord} <> ${aiWord}`;
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return `Today, ${format(date, 'h:mma').toLowerCase()}`;
    } else {
      return format(date, 'MMM d, h:mma').replace(/^[a-z]/, match => match.toUpperCase()).replace(/am|pm/g, match => match.toLowerCase());
    }
  };

  return (
    <div className="space-y-4">
      {loading ? (
        <p className="text-muted-foreground text-center py-8">
          Loading history...
        </p>
      ) : history.length === 0 ? (
        <p className="text-muted-foreground text-center py-8">
          {user ? "You haven't played any games yet. Start a new game!" : "No games played yet."}
        </p>
      ) : (
        <div className="space-y-4">
          {history.map(game => (
            <motion.div 
              key={game.id} 
              initial={{ opacity: 0, y: 10 }} 
              animate={{ opacity: 1, y: 0 }} 
              className="p-4 bg-card rounded shadow-sm border border-border cursor-pointer transition-colors hover:bg-accent/30" 
              onClick={() => navigateToGame(game.id)}
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-foreground">
                    {formatGameTitle(game)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {game.type} - {formatDateTime(game.created_at)}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <div className="flex items-center gap-2">
                    {game.status === 'completed' ? (
                      <span className="text-sm font-medium text-green-600 flex items-center gap-1">
                        <CheckCircle size={14} />
                        Converged!
                      </span>
                    ) : game.status === 'diverged' ? (
                      <span className="text-sm font-medium text-red-600 flex items-center gap-1">
                        <XCircle size={14} />
                        Diverged
                      </span>
                    ) : (
                      <span className="text-sm font-medium text-amber-600">
                        In progress
                      </span>
                    )}
                  </div>
                  
                  <div className="text-sm text-muted-foreground">
                    {game.status === 'completed' ? `${game.rounds} Rounds` : 
                     game.status === 'diverged' ? `20 Rounds` :
                     `Round ${game.rounds}`}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
          
          {/* Loading indicator and intersection observer target */}
          {hasMore && (
            <div 
              ref={loadMoreRef} 
              className="py-4 flex justify-center"
            >
              {loadingMore && (
                <div className="flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading more...</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
